import { Box, Container, Loader, Stack, Title } from '@mantine/core'
import { AppText } from '../../app/common/ui'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import agent from '../../app/api/agent'
import type { LegalTextDto } from '../../app/models/legalText'
import GunSlopWordmark from '../../app/common/ui/GunSlopWordmark'

const PAGE_TITLES: Record<string, string> = {
  'terms-of-service': 'Terms of Service',
  'privacy-policy': 'Privacy Policy',
  'community-guidelines': 'Community Guidelines'
}

interface LegalTextPageProps {
  legalKey: string
}

export default function LegalTextPage ({ legalKey }: LegalTextPageProps) {
  const [text, setText] = useState<LegalTextDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await agent.LegalText.getByKey(legalKey)
        if (!cancelled) setText(result)
      } catch {
        if (!cancelled) setError('Unable to load this page. Please try again later.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [legalKey])

  const pageTitle = PAGE_TITLES[legalKey] ?? 'Legal'

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--gs-bg)' }}>
      <Container size="sm" py="xl">
        <Stack spacing="lg">
          <Title order={1} sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 28, color: 'var(--gs-text)' }}>
            {pageTitle}
          </Title>

          {loading && (
            <Box sx={{ textAlign: 'center', padding: 40 }}>
              <Loader color="orange" />
            </Box>
          )}

          {!loading && error && (
            <AppText sx={{ color: 'var(--gs-muted)', fontSize: 14 }}>{error}</AppText>
          )}

          {!loading && text && (
            <>
              <AppText size="xs" sx={{ color: 'var(--gs-muted)' }}>
                Version {text.version}
                {text.lastUpdatedAt && ` · Last updated ${new Date(text.lastUpdatedAt).toLocaleDateString()}`}
              </AppText>
              {legalKey === 'privacy-policy' && (
                <AppText size="sm" sx={{ color: 'var(--gs-muted)', lineHeight: 1.6 }}>
                  Subprocessor register and DPA inventory: request the current list at{' '}
                  <a href="mailto:privacy@gunslop.com" style={{ color: '#58A6FF' }}>privacy@gunslop.com</a>
                  {' '}(repository path: docs/subprocessor-register.md).
                </AppText>
              )}
              <AppText
                component="pre"
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: 'var(--gs-text)',
                  whiteSpace: 'pre-wrap',
                  margin: 0
                }}
              >
                {text.body}
              </AppText>
            </>
          )}

          <AppText
            size="sm"
            component="div"
            sx={{ color: 'var(--gs-muted)', paddingTop: 16, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}
          >
            <Link to="/" style={{ color: '#58A6FF', textDecoration: 'none' }}>
              ← Back to
            </Link>
            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
              <GunSlopWordmark size="xs" inline sx={{ fontSize: 14, color: '#58A6FF' }} slopColor="#58A6FF" />
            </Link>
          </AppText>
        </Stack>
      </Container>
    </Box>
  )
}
